import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const ZOOM_SIZE = 240; // px
const OFFSET = 24; // px offset from cursor

function getPositionWithinViewport(x, y, width, height) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = x + OFFSET;
  let top = y + OFFSET;
  if (left + width > vw) left = vw - width - 8;
  if (top + height > vh) top = vh - height - 8;
  if (left < 0) left = 8;
  if (top < 0) top = 8;
  return { left, top };
}

function getCenteredPosition() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    left: Math.max(8, (vw - ZOOM_SIZE) / 2),
    top: Math.max(8, (vh - ZOOM_SIZE) / 2)
  };
}

export default function ConnectorImageZoom({ src, alt, style = {}, ...props }) {
  const [zoom, setZoom] = useState(false);
  const [zoomMode, setZoomMode] = useState(null); // 'hover' | 'click' | null
  const [zoomPos, setZoomPos] = useState({ left: 0, top: 0 });
  const hoverCloseTimeoutRef = useRef(null);

  const clearHoverCloseTimeout = () => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  };

  const closeZoom = () => {
    clearHoverCloseTimeout();
    setZoom(false);
    setZoomMode(null);
  };

  const handleMouseEnter = (e) => {
    if (zoomMode === 'click') return;
    clearHoverCloseTimeout();
    setZoomMode('hover');
    setZoom(true);
    const { left, top } = getPositionWithinViewport(e.clientX, e.clientY, ZOOM_SIZE, ZOOM_SIZE);
    setZoomPos({ left, top });
  };

  const handleMouseMove = (e) => {
    if (zoomMode !== 'hover') return;
    const { left, top } = getPositionWithinViewport(e.clientX, e.clientY, ZOOM_SIZE, ZOOM_SIZE);
    setZoomPos({ left, top });
  };

  const handleMouseLeave = () => {
    if (zoomMode !== 'hover') return;
    // Brief delay avoids flicker from sub-pixel / layout shifts on parent hover styles.
    clearHoverCloseTimeout();
    hoverCloseTimeoutRef.current = setTimeout(closeZoom, 40);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (zoomMode === 'hover') {
      closeZoom();
      return;
    }
    if (zoom) {
      closeZoom();
    } else {
      setZoomPos(getCenteredPosition());
      setZoomMode('click');
      setZoom(true);
    }
  };

  return (
    <>
      <img
        src={src}
        alt={alt}
        style={{ width: 60, height: 60, objectFit: 'contain', cursor: 'zoom-in', display: 'block', ...style }}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        {...props}
      />
      {zoom && ReactDOM.createPortal(
        <>
          {zoomMode === 'click' && (
            <div
              role="button"
              tabIndex={0}
              aria-label="Close zoom"
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 12999,
                background: 'transparent',
                cursor: 'pointer',
              }}
              onClick={closeZoom}
              onKeyDown={(e) => e.key === 'Escape' && closeZoom()}
            />
          )}
          <div
            style={{
              position: 'fixed',
              left: zoomPos.left,
              top: zoomPos.top,
              width: ZOOM_SIZE,
              height: ZOOM_SIZE,
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              zIndex: 13000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              border: '2px solid #eee',
            }}
          >
            <img
              src={src}
              alt={alt}
              style={{ width: '90%', height: '90%', objectFit: 'contain', borderRadius: 8 }}
              draggable={false}
            />
          </div>
        </>,
        document.body
      )}
    </>
  );
}
